import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { storeAPI } from '../../services/api';
import { useCart } from '../../context/CartContext';
import { ArrowLeft, SlidersHorizontal, ChevronDown, Filter } from 'lucide-react';
import ProductCard from '../../components/store/ProductCard';

const CategoryProducts = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [products, setProducts] = useState([]);
  const [category, setCategory] = useState(null);
  const [tree, setTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const activeSubcategory = searchParams.get('subcategory');
  const activeFamily = searchParams.get('family');

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const [catRes, treeRes] = await Promise.all([
          storeAPI.getCategories(),
          storeAPI.getCategoryTree(),
        ]);
        const found = catRes.data.find((c) => c.slug === slug);
        setCategory(found || null);
        setTree(treeRes.data);

        const params = {};
        if (activeSubcategory) params.subcategory_id = activeSubcategory;
        if (activeFamily) params.family_id = activeFamily;
        const prodRes = await storeAPI.getCategoryProducts(slug, params);
        setProducts(prodRes.data);
      } catch {
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [slug, activeSubcategory, activeFamily]);

  const handleAddToCart = async (product) => {
    try {
      await addItem(product.id);
    } catch {
      navigate('/login');
    }
  };

  const currentCat = tree.find(c => c.slug === slug);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="max-w-7xl mx-auto px-4 py-8">
      <motion.button initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }}
        onClick={() => navigate('/store')}
        className="flex items-center gap-2 text-gray-600 dark:text-gray-300 hover:text-orange-600 mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" /> Volver a Tienda
      </motion.button>

      <div className="flex items-center justify-between mb-8">
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{category?.name || 'Cargando...'}</h1>
          {category && (
            <span className="text-sm text-gray-500 dark:text-gray-400">{category.is_physical ? 'Productos Físicos' : 'Productos Digitales'}</span>
          )}
        </motion.div>
        <div className="flex items-center gap-2">
          <button onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 border border-gray-200 dark:border-gray-600 rounded-xl text-gray-500 hover:text-orange-600 lg:hidden"
          ><Filter className="w-4 h-4" /></button>
          <SlidersHorizontal className="w-4 h-4 text-gray-500" />
          <select value={`${sortBy}-${sortOrder}`} onChange={(e) => { const [sb, so] = e.target.value.split('-'); setSortBy(sb); setSortOrder(so); }}
            className="text-sm border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-800"
          >
            <option value="created_at-desc">Más recientes</option>
            <option value="price-asc">Menor precio</option>
            <option value="price-desc">Mayor precio</option>
            <option value="name-asc">A-Z</option>
          </select>
        </div>
      </div>

      <div className="flex gap-8">
        <motion.aside animate={{ width: sidebarOpen ? 260 : 0 }} className={`${sidebarOpen ? 'w-0 lg:w-64' : 'hidden lg:block lg:w-64'} shrink-0`}>
          <div className={`${sidebarOpen ? '' : 'hidden'} lg:block sticky top-24 space-y-6`}>
            {currentCat?.subcategories?.map(sub => (
              <div key={sub.id} className="mb-4">
                <button onClick={() => navigate(`/store/categories/${slug}?subcategory=${sub.id}`)}
                  className={`w-full text-left px-4 py-2.5 rounded-xl text-sm font-medium transition-colors ${activeSubcategory == sub.id ? 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' : 'text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'}`}
                >{sub.name}</button>
                {sub.families?.length > 0 && activeSubcategory == sub.id && (
                  <div className="ml-4 mt-1 space-y-1">
                    {sub.families.map(fam => (
                      <button key={fam.id} onClick={() => navigate(`/store/categories/${slug}?family=${fam.id}`)}
                        className={`w-full text-left px-4 py-1.5 rounded-lg text-xs transition-colors ${activeFamily == fam.id ? 'text-orange-600 font-medium' : 'text-gray-400 hover:text-orange-500'}`}
                      >{fam.name}</button>
                    ))}
                  </div>
                )}
              </div>
            ))}
            {(activeSubcategory || activeFamily) && (
              <button onClick={() => navigate(`/store/categories/${slug}`)}
                className="w-full text-center text-xs text-orange-500 hover:text-orange-600 py-2"
              >Limpiar filtros</button>
            )}
          </div>
        </motion.aside>

        <div className="flex-1 min-w-0">
          {loading ? (
            <div className="flex justify-center py-20">
              <motion.div animate={{ rotate: 360 }} transition={{ repeat: Infinity, duration: 1, ease: "linear" }}
                className="w-12 h-12 border-4 border-orange-200 border-t-orange-600 rounded-full" />
            </div>
          ) : products.length === 0 ? (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-20">
              <p className="text-gray-500 dark:text-gray-400">No hay productos en esta categoría</p>
            </motion.div>
          ) : (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.15 }}
              className="grid grid-cols-2 md:grid-cols-3 gap-6"
            >
              {products.map((product) => (
                <ProductCard key={product.id} product={product} onAddToCart={handleAddToCart} />
              ))}
            </motion.div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default CategoryProducts;
